/**
 * CommunityGrid Component
 * 2-column responsive grid of community cards with infinite scroll
 */

import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { BookmarkFilledIcon, BookmarkIcon } from "@/components/icons"

import { formatLocation } from "@/components/shared/formatLocation"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useToggleCommunitySave } from "@/features/profile/hooks/useSavedCommunities"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { cn } from "@/lib"
import type { ConnectCommunity } from "../types/connect.types"

interface CommunityGridProps {
  communities: ConnectCommunity[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
}

function ExploreCard({
  community,
  onShowLogin,
}: {
  community: ConnectCommunity
  onShowLogin: () => void
}) {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const location = formatLocation(community.city, community.state)
  const coverImage = community.coverImage ?? ""
  const toggleSave = useToggleCommunitySave()

  // Track locally-toggled IDs so we can show the icon flip instantly
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set())

  // biome-ignore lint/correctness/useExhaustiveDependencies: community.id is intentionally watched for reset
  useEffect(() => {
    setToggledIds(new Set())
  }, [community.id])

  const isSaved =
    community.isSaved !== undefined
      ? toggledIds.has(community.id)
        ? !community.isSaved
        : community.isSaved
      : false

  const handleBookmark = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (!user) {
        onShowLogin()
        return
      }

      setToggledIds(prev => {
        const next = new Set(prev)
        if (next.has(community.id)) {
          next.delete(community.id)
        } else {
          next.add(community.id)
        }
        return next
      })

      toggleSave.mutate(community.id, {
        onError: () => {
          setToggledIds(prev => {
            const next = new Set(prev)
            next.delete(community.id)
            return next
          })
        },
      })
    },
    [community.id, toggleSave, user, onShowLogin],
  )

  const handleClick = () => {
    navigate(`/communities/${community.slug || community.id}`)
  }

  return (
    <div
      className="relative aspect-[16/11] rounded-[20px] overflow-hidden group cursor-pointer"
      onClick={handleClick}
    >
      {/* Cover Image */}
      {coverImage ? (
        <img
          src={coverImage}
          alt={community.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-card" />
      )}

      {/* Save Button */}
      <button
        type="button"
        onClick={handleBookmark}
        className={cn(
          "absolute top-5 right-5 z-10 shrink-0 cursor-pointer rounded-[10px] border p-2.75 transition-colors shadow-[0_5px_10px_rgba(2,2,2,0.30)] backdrop-blur-[10px] border-gradient-glass",
          isSaved
            ? "border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.1)]"
            : "border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] hover:bg-[rgba(242,240,235,0.1)]",
        )}
        aria-label={isSaved ? "Unsave community" : "Save community"}
      >
        {isSaved ? (
          <BookmarkFilledIcon className="w-[22px] h-[22px] fill-white text-white" />
        ) : (
          <BookmarkIcon className="w-[22px] h-[22px] text-primary" />
        )}
      </button>

      {/* Bottom Gradient Overlay */}
      <div className="absolute inset-x-0 -bottom-2.5  bg-gradient-to-t from-black/80 to-transparent backdrop-blur-sm mb-4">
        <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex flex-col">
          {/* Name */}
          <span className="text-[18px] font-semibold text-text-primary opacity-90 truncate">
            {community.name}
          </span>

          {/* Members + Rituals */}
          <div className="flex items-center gap-[10px] text-[13px] text-text-primary opacity-70 mt-1">
            <span>{community.memberCount} Members</span>
            <span>{community.ritualCount} Rituals</span>
          </div>

          {/* Location */}
          {location && (
            <span className="text-[13px] text-text-primary opacity-70 truncate mt-2">
              {location}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function CommunityGrid({ communities, isLoading, hasMore, onLoadMore }: CommunityGridProps) {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const loadMoreRef = useInfiniteScroll({
    onLoadMore,
    hasMore,
    threshold: 200,
  })

  return (
    <div className="mt-6">
      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {communities.map(community => (
          <ExploreCard
            key={community.id}
            community={community}
            onShowLogin={() => setShowLoginModal(true)}
          />
        ))}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="aspect-[16/11] bg-card rounded-[20px] animate-pulse" aria-hidden="true" />
          <div className="aspect-[16/11] bg-card rounded-[20px] animate-pulse" aria-hidden="true" />
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      {(hasMore || isLoading) && <div ref={loadMoreRef} className="h-4" aria-hidden="true" />}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
